<script>
   /*********************************************************************/
   /* Contributions modal                                               */
   /* --------------------                                              */
   /* plot variables contributions to q/h distances for selected object */
   /*********************************************************************/

   import { Multilines, Rectangles, Points, Lines, TextLabels } from 'svelte-plots-basic/2d';
   import { scale as prep_scale } from 'mdatools/prep';
   import { Matrix, Vector, vector, crossprod } from 'mdatools/arrays';
   import { range } from 'mdatools/stat';

   import { pcamodel_get } from '../wasm/dist/pca.js';
   import { Container, Select } from 'svelte-controls-basic';
   import { derived } from 'svelte/store';
   import Modal from './Modal.svelte';
   import {excludeVars}  from '../utils.js';

   import Axes from '../plots/Axes.svelte';


   let {
      dataset,
      model,
      selected,
      ncomp,
      closeModal,
      outliers = [],
      getter = pcamodel_get,
   } = $props();

   // type of the contributions to show
   let type = $state('residual');

   // index and x-coordinate of the selected variable
   let sel = $state(-1);

   // number of predictors and responses
   const npred = $derived(model.npred ? model.npred : model.ncols);

   // get X part of the data
   function getX(dataset) {
      const d = (model.exclvars  && model.exclvars.length > 0) ? excludeVars(dataset, model.exclvars) : dataset;
      if (!d.nresp || d.nresp < 1) return d.values;
      const offset = d.nresp * d.values.nrows;
      const nrows = d.values.nrows;
      const npred = d.values.ncols - d.nresp;
      return new Matrix(d.values.v.subarray(offset), nrows, npred);
   }

   // mouse handle for selection of variables
   function mouseHandler(e) {

      if (e.target.tagName !== 'rect') {
         sel = -1;
         return;
      }

      const id = Array.from(e.target.parentNode.children).indexOf(e.target);
      if (id < 0) {
         sel = -1;
         return;
      }

      // if index is the same as the highlighted variable, do nothing
      if (sel === id) return;
      sel = id;
   }


   // y-coordinate of selected point and label
   const hx = $derived(xc && sel >= 0 ? xc.values.v[sel] : null);
   const hy = $derived(yc && sel >= 0 ? yc.sel.v[sel] : null);
   const hl = $derived(model && sel >= 0 ? model.varlabels[sel] : null);


   // set x-axis values and limits
   const xc = $derived.by(() => {
      if (!model) return null;
      const nresp = !model.nresp ? 0 : model.nresp;
      const values = vector(model.varvalues.filter((v, i) => i >= nresp));
      const n = values.length;                                      // number of variables
      const dil = (values.v[1] - values.v[0]) / 2;                 // half distance between two first x-variables
      const dir = (values.v[n - 1] - values.v[n - 2]) / 2;         // half distance between two last x-variables
      const rng = [values.v[0], values.v[n - 1]];                 // range of x-variables (assuming they are ordered)
      const lim = [rng[0] - dil, rng[1] + dir];
      const width = (rng[1] - rng[0]) / n;
      return {values, lim, width, hwidth: width * 0.5}
   });

   // compute coordinates of rectangles for selection if necessary
   const left = $derived(xc ? xc.values.apply(v => v - xc.hwidth) : null);

   // preprocessed data
   const Xpt = $derived.by(() => {
      if (!dataset || !model) return null;
      const X = getX(dataset, model.exclvars);
      const m = new Vector(getter(model, "mX"));
      const s = new Vector(getter(model, "sX"));
      return prep_scale(X, m, s).t();
   });

   // main outcomes
   const Pt = $derived(model && ncomp > 0 && ncomp <= model.ncomp ?
      new Matrix(getter(model, 'P', ncomp), npred, ncomp).t() :
      null
   );

   const Tt = $derived(Xpt && Pt ? Pt.dot(Xpt) : null);
   const Ht = $derived(Tt ? crossprod(Pt, Tt) : null);
   const Qt = $derived(Ht ? Xpt.subtract(Ht) : null);

   let h = $derived(Ht ? Ht.getcolumn(selected + 1) : null);
   let q = $derived(Qt ? Qt.getcolumn(selected + 1) : null);
   let xp = $derived(Xpt ? Xpt.getcolumn(selected + 1) : null);

   let yc = $derived.by(() => {
      if (!h || !q || !xp) return null;
      const vc =  type === 'explained' ?  {all: Ht, sel: h, label: 'Explained part, TP^T'} :
         (type === 'residual' ? {all: Qt, sel: q, label: 'Residuals, E'} :
         {all: Xpt, sel: xp, label: 'Preprocessed data, X'});

      const rng = range(vc.all.v);
      const height = rng[1] - rng[0];
      const lim = [rng[0] - height/50, rng[1] + height/12];
      const top = Vector.fill(rng[1], vc.sel.length);

      vc['lim'] = lim;
      vc['top'] = top;
      vc['height'] = height;
      return vc;
   });

   // set plot title
   const title = $derived(dataset && selected >= 0 ? `Object ${dataset.objlabels[selected].replace("_", "-")}` : null);
   const xLabel = $derived(model ? model.varvaluesName + (model.varvaluesUnits ? ', ' + model.varvaluesUnits : '') : null);
</script>

{#if xc && yc}
<Modal {closeModal}>
   <div class="wrapper">
   <!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
   <!-- svelte-ignore a11y_click_events_have_key_events -->
   <div class="plot-container" role="tooltip" onclick={mouseHandler}>
      <Axes {title} yLabel={yc.label} {xLabel} limY={yc.lim} limX={xc.lim} fileName='plot-contributions' >

      <!-- all outcomes -->
      <Multilines xValues={xc.values} yValues={yc.all} lineColor='#e0e0e0'/>

      <!-- outliers — show using diffeent color -->
      {#if outliers.length > 0}
         {#each outliers as ind}
         <Lines xValues={xc.values} yValues={yc.all.getcolumn(ind + 1)} lineColor="#ffdd88" />
         {/each}
      {/if}

      <!-- outcomes for the selected objects -->
      <Lines xValues={xc.values} yValues={yc.sel} lineColor="crimson"  />

      <!-- rectangles to make selection possible -->
      <Rectangles {left} top={yc.top} height={yc.height} width={xc.width} faceColor='transparent' borderColor='transparent'/>

      {#if sel >= 0}
         <!-- highlighted variable -->
         <TextLabels xValues={[hx]} yValues={[yc.top.v[0]]} labels={[hl]} pos={3} textSize={0.85}/>
         <Points xValues={[hx]} yValues={[hy]} faceColor='crimson' lineColor='crimson'/>
         <Rectangles left={[left.v[sel]]} top={[yc.top.v[sel]]} height={yc.height} width={xc.width} faceColor='#ff000015'/>
      {/if}
   </Axes>
   </div>

   <div class="msg-select">click inside the plot to see a variable label</div>
   <div class="plot-controls">
      <Container label=''>
         <Select id='type' bind:value={type} options={['full', 'explained', 'residual']} />
      </Container>
   </div>
   </div>
</Modal>
{/if}

<style>
   .wrapper {
      width: 95vw;
      height: 90vh;
      display: grid;
      grid-template-areas:
         "plot"
         "message"
         "controls";
      grid-template-rows: 1fr 15px min-content;
   }

   .plot-container {
      grid-area: plot;
   }

   .plot-controls {
      grid-area: controls;
      display: flex;
      width: 100%;
      justify-content: center;
      border-radius: 0.5rem;
   }

   .plot-controls > :global(.control-element) {
      background: transparent;
   }

   .msg-select {
      grid-area: message;
      height: 15px;
      font-size: 12px;
      color: #aaa;
      text-align: center;
   }
</style>